<template>
  <div class="tw-mb-4">
    <label class="control-label">{{ $t('Values') }}</label>
    <div class="f-12 tw-text-gray-600">
      {{ $tc(':count values added', localOptions.length) }}
    </div>
    <InputTags v-model="localOptions" :name="'values'" :placeholder="$t('Values here')" multiple>
      <template #default="{ localValue, withDelete, deselect }">
        <span class="tag label label-info tag-width-constrained tw-m-0">
          <WdsStack row>
            <span class="tag-value tw-mr-1">{{ localValue }}</span>
            <WdsIcon
              v-if="withDelete"
              :size="14"
              name="trash-can"
              class="tw-text-white"
              @click="deselect(localValue)"
            />
          </WdsStack>
        </span>
      </template>
      <template #between-input-tags>
        <div v-if="error" class="tw-mb-2.5">
          <div class="tw-text-red-500">
            <span>{{ error }}</span>
          </div>
        </div>
        <div class="tw-text-xs tw-mb-2.5 tw-text-gray-600">
          {{
            $t(
              'Add a value by pressing enter. Values separated with semicolon (;) will be added as separate values.',
            )
          }}
        </div>
      </template>
    </InputTags>
    <WdsButtonGhost @click="handleAlphabetize">
      <div class="tw-flex tw-items-center">
        <WdsTooltip
          :text="$t('Rearrange the values entered to be displayed in alphabetical order')"
          class="tw-mr-2"
        >
          <WdsIcon name="info" :size="16" />
        </WdsTooltip>
        {{ $t('Alphabetize values') }}
      </div>
    </WdsButtonGhost>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { WdsIcon, WdsStack, WdsButtonGhost, WdsTooltip } from '@worksome/wds-components'
import { CustomFieldOption } from '@/components/Fields/dataObjects/CustomFieldOption'
import InputTags from '@/components/InputTags.vue'
import { $t, $tc } from '@/composables/translation'

const emits = defineEmits(['update:modelValue'])

interface Props {
  modelValue: Array<CustomFieldOption>
  error?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  error: null,
})

function reorderOptions(newFieldOptions: Array<string>): Array<CustomFieldOption> {
  return newFieldOptions.map((newFieldOption) => {
    const n = props.modelValue
      .map((fieldOption: CustomFieldOption) => fieldOption.value)
      .indexOf(newFieldOption)
    return props.modelValue[n]
  })
}

function addNewOptions(newFieldOptions: Array<string>): Array<CustomFieldOption> {
  const fieldOptions: Array<CustomFieldOption> = []
  props.modelValue.forEach((fieldOption: CustomFieldOption) => fieldOptions.push(fieldOption))
  newFieldOptions
    .filter(
      (fieldOption: string) =>
        !props.modelValue
          .map((mapOption) => mapOption.value)
          .find((findOption) => findOption === fieldOption),
    )
    .forEach((fieldOption) => fieldOptions.push(new CustomFieldOption({ value: fieldOption })))

  return fieldOptions
}

function removeOptions(newFieldOptions): CustomFieldOption[] {
  const fieldOptions = [] as CustomFieldOption[]

  props.modelValue.forEach((fieldOption) => {
    if (newFieldOptions.find((findOption) => findOption === fieldOption.value)) {
      fieldOptions.push(fieldOption)
    }
  })

  return fieldOptions
}

const localOptions = computed({
  get: (): Array<string> =>
    props.modelValue.map((option: CustomFieldOption) => option.value as string),
  set: (newOptions) => {
    if (newOptions.length === 1 && newOptions[0] === undefined) {
      return
    }
    let updatedOptions: Array<CustomFieldOption>
    if (newOptions.length === props.modelValue.length) {
      updatedOptions = reorderOptions(newOptions)
    } else if (newOptions.length > props.modelValue.length) {
      updatedOptions = addNewOptions(newOptions)
    } else {
      updatedOptions = removeOptions(newOptions)
    }
    emits('update:modelValue', updatedOptions)
  },
})

const handleAlphabetize = () => {
  const alphabetizedOptions = props.modelValue.toSorted((optionA, optionB) =>
    optionA.value.toLowerCase() < optionB.value.toLowerCase() ? -1 : 1,
  )

  emits('update:modelValue', alphabetizedOptions)
}
</script>
